import React, { useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { apiService } from '../../../client/common/apiCallService';
import ExcelJS from 'exceljs';
import { format } from 'date-fns';
import {
    getCurrencySymbol,
    getExChangeAmount,
    getTransectionDetails,
    toFixedTrunc,
} from '../../../client/Juno/helpers';
import { constant } from '../../../client/Juno/client/constant';
import MoreVertOutlinedIcon from '@mui/icons-material/MoreVertOutlined';
import ArrowForwardIosOutlinedIcon from '@mui/icons-material/ArrowForwardIosOutlined';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import ActionConfirmModal from '../common/ActionConfirmModal';
import useDeleteStatments from './useDeleteStatements';
import { Box, Popover } from '@mui/material';

export default function StatementsTableActions(props) {
    const downloadMenuRef = useRef(null);
    const { row } = props;
    const API_URL = useSelector(state => state.config.api_url);
    const imageBaseUrl = useSelector(state => state.config.image_base_url);
    const activeCurrencyPage = localStorage.getItem('activeCurrencyPage');
    const clientData = useSelector(state => state.juno.clientData);
    const readOnly = clientData && clientData.readOnly ? clientData.readOnly : false;

    const [anchorEl, setAnchorEl] = useState(null);
    const [downloadAnchorEl, setDownloadAnchorEl] = useState(null);

    const open = Boolean(anchorEl);
    const downloadOpen = Boolean(downloadAnchorEl);

    const handleClick = event => setAnchorEl(event.currentTarget);
    const handleClose = () => setAnchorEl(null);

    const handleDownloadClick = event => setDownloadAnchorEl(event.currentTarget);
    const handleDownloadClose = () => setDownloadAnchorEl(null);

    const [selectedStatment, setSelectedStatement, deleteStatement] = useDeleteStatments();

    const downloadExcel = (blob, fileName) => {
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = fileName;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
    };

    const generateExcel = async data => {
        const { fromDate, endDate, currency, transactionDetails, openingBalance, closingBalance } =
            data;
        const startingDate = format(fromDate, 'dd MMM');
        const endingDate = format(endDate, 'dd MMM');
        const workbook = new ExcelJS.Workbook();
        const worksheet = workbook.addWorksheet('Account Statement');

        const addFormattedRow = (
            rowData,
            mergeCellsRange = null,
            isBold = false,
            alignment = {},
        ) => {
            const row = worksheet.addRow(rowData);
            row.eachCell({ includeEmpty: true }, cell => {
                cell.alignment = { wrapText: true, ...alignment };
                if (isBold) cell.font = { bold: true };
            });
            if (mergeCellsRange && !worksheet.getCell(mergeCellsRange).isMerged) {
                worksheet.mergeCells(mergeCellsRange);
            }
            return row;
        };

        const setColumns = () => {
            worksheet.columns = [
                { key: 'Date', width: 15 },
                { key: 'Time', width: 15 },
                { key: 'Type', width: 20 },
                { key: 'Details', width: 25 },
                { key: 'Reference', width: 25 },
                { key: 'Amount', width: 25 },
                { key: 'Balance', width: 25 },
            ];
        };

        addFormattedRow([`${currency} Statement`], 'A1:G1', true, {
            vertical: 'top',
            horizontal: 'left',
        });
        addFormattedRow(
            ['Date', 'Time', 'Type', 'Details', 'Reference', 'Amount', 'Balance'],
            null,
            true,
        );

        if (transactionDetails.length === 0) {
            const noDataRow = addFormattedRow(
                [startingDate, '', '', 'No transactions available for this date range', '', '', ''],
                'A3:G3',
                true,
                { horizontal: 'center' },
            );
        } else {
            addFormattedRow([
                startingDate,
                '',
                '',
                'Statement Opening Balance',
                '',
                '',
                '',
                `${getCurrencySymbol(currency)} ${toFixedTrunc(openingBalance, activeCurrencyPage === 'crypto' ? 8 : 2)}`,
            ]);

            transactionDetails.filter(Boolean).forEach(row => {
                const feeText =
                    row?.type === 'FX' ||
                        row?.currencyType === 'crypto' ||
                        row?.type === constant.TRANSACTION_TYPE_CONSTANT.CRYPTO.BUY ||
                        row?.type === constant.TRANSACTION_TYPE_CONSTANT.CRYPTO.SELL
                        ? ''
                        : `Fee: ${getCurrencySymbol(currency)} ${toFixedTrunc(row?.transactionRef?.transactionFee || row?.transactionRef?.transferInFee) || 0.0}`;

                const getTransactionType = row => {
                    const { type, transactionRef } = row;
                    if (type === constant.TRANSACTION_TYPE_CONSTANT.FIAT.TRANSFER) {
                        return `Transfer (${transactionRef?.transferInOut || ''})`;
                    }
                    if (activeCurrencyPage === 'fiat') {
                        if (type === constant.TRANSACTION_TYPE_CONSTANT.CRYPTO.BUY)
                            return 'Transfer (Out)';
                        if (type === constant.TRANSACTION_TYPE_CONSTANT.CRYPTO.SELL)
                            return 'Transfer (In)';
                    }
                    return type || 'N/A';
                };

                const newRow = worksheet.addRow([
                    format(row.creationDate, 'dd MMM'),
                    new Date(row.creationDate).toLocaleTimeString(),
                    `${getTransactionType(row)}${feeText ? `\n${feeText}` : ''}`,
                    getTransectionDetails(row, activeCurrencyPage),
                    row.transactionRef?.transactionDetails?.[0]?.reference || 'N/A',
                    getExChangeAmount(row, activeCurrencyPage),
                    `${getCurrencySymbol(currency)} ${toFixedTrunc(row.balanceAmount, activeCurrencyPage === 'crypto' ? 8 : 2)}`,
                ]);
                newRow.eachCell(
                    { includeEmpty: true },
                    cell => (cell.alignment = { wrapText: true }),
                );
            });
        }

        addFormattedRow([
            endingDate,
            '',
            '',
            'Statement Closing Balance',
            '',
            '',
            '',
            `${getCurrencySymbol(currency)} ${toFixedTrunc(closingBalance, activeCurrencyPage === 'crypto' ? 8 : 2)}`,
        ]);

        setColumns();
        const buffer = await workbook.xlsx.writeBuffer();
        const blob = new Blob([buffer], {
            type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        });
        downloadExcel(blob, 'Account_Statement.xlsx');
    };

    const onDownload = async (row, downloadType = 'pdf') => {
        handleClose();
        handleDownloadClose();
        apiService(
            `${window.location.hostname === 'localhost' ? API_URL : ''}/restapi/generate-statement-pdf`,
            {
                statementId: row._id,
                fromDate: row.timePeriod.fromDate || row.timePeriod.formDate,
                endDate: row.timePeriod.endDate,
                clientId: row.clientId,
                type: row.type,
                currency: row.currency,
                activeCurrencyPage,
                downloadType,
            },
            async data => {
                if (data) {
                    if (downloadType === 'pdf') {
                        const newTab = window.open();
                        newTab?.document.write(data);
                    } else {
                        await generateExcel(data);
                    }
                }
            },
            err => console.error(err),
        );
    };

    const menuOptions = [
        {
            label: 'PREVIEW',
            onClick: () => {
                handleClose();
                onDownload(row);
            },
        },
        {
            label: 'DOWNLOAD',
            onClick: event => {
                setDownloadAnchorEl(event.currentTarget);
            },
            icon: <ArrowForwardIosOutlinedIcon className="!ml-auto !text-xs" />,
        },
        readOnly
            ? null
            : {
                label: 'delete',
                onClick: () => {
                    handleClose();
                    setSelectedStatement(row);
                },
            },
    ].filter(Boolean);

    return (
        <>
            <div>
                <button onClick={handleClick}>
                    <MoreVertOutlinedIcon className="!h-[19px] " />
                </button>
                <div>
                    <Menu
                        anchorEl={anchorEl}
                        open={Boolean(anchorEl)}
                        onClose={handleClose}
                        slotProps={{
                            paper: {
                                sx: {
                                    backgroundColor: 'var(--common-modal-bg)',
                                    borderRadius: '8px',
                                    minWidth: '160px',
                                    border: '2px solid #3F3F46'
                                },
                            },
                        }}
                        MenuListProps={{
                            sx: {
                                paddingTop: 0,
                                paddingBottom: 0,
                            },
                        }}>
                        <div>
                            {menuOptions.map((item, index) => (
                                <MenuItem
                                    key={index}
                                    onClick={item.onClick}
                                    ref={item.label === 'DOWNLOAD' ? downloadMenuRef : null}
                                    className="!text-text uppercase"
                                    sx={{
                                        fontSize: '14px',
                                        fontWeight: 500,
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'center',
                                        '&:hover': {
                                            backgroundColor: 'var(--card-bg)',
                                        },
                                        paddingTop: '10px',
                                        paddingBottom: '10px',
                                        '@media (prefers-color-scheme: dark)': {
                                            color: 'black',
                                        },
                                    }}>
                                    {item.label}
                                    {item.icon}
                                </MenuItem>
                            ))}
                        </div>
                    </Menu>
                </div>
                <Popover
                    open={Boolean(downloadAnchorEl)}
                    anchorEl={downloadMenuRef.current}
                    onClose={handleDownloadClose}
                    anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
                    transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                    PaperProps={{
                        sx: {
                            backgroundColor: 'var(--common-modal-bg)',
                            borderRadius: '8px',
                            minWidth: '160px',
                            boxShadow: '0px 0px 12px rgba(0, 0, 0, 0.4)',
                            ml: '-3px',
                            border: '2px solid #3F3F46'
                        },
                    }}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', padding: 0 }}>
                        {['pdf', 'excel'].map((type) => (
                            <Box
                                key={type}
                                onClick={() => {
                                    onDownload(row, type);
                                    handleDownloadClose();
                                }}
                                sx={{
                                    fontFamily: 'Inter',
                                    cursor: 'pointer',
                                    color: 'var(--color-text) !important',
                                    fontSize: '13px',
                                    paddingY: '10px',
                                    paddingX: '16px',
                                    textTransform: 'uppercase',
                                    '&:hover': {
                                        backgroundColor: 'var(--card-bg)',
                                    },
                                    '@media (prefers-color-scheme: dark)': {
                                        color: '#ffffff',
                                    },
                                }}>
                                {type} file
                            </Box>
                        ))}
                    </Box>
                </Popover>
            </div>
            {selectedStatment ? (
                <ActionConfirmModal
                    open={Boolean(selectedStatment)}
                    type={'delete'}
                    onCancel={() => setSelectedStatement(null)}
                    heading="delete statement!"
                    subHeading={'are you sure you want to delete the statement?'}
                    onConfirm={() => deleteStatement(row._id)}
                    body={() => (
                        <div className="text-center justify-center text-text text-base font-semibold font-['Inter'] uppercase leading-normal">
                            {row.currency} statement
                        </div>
                    )}
                    actionButtonText="delete"
                />
            ) : null}
        </>
    );
}
